import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { ApplicationRoutes } from '@foxera/shared/constants/application.routes';
import { PermissionService } from '@foxera/shared/services/permission.service';

/**
 * Creates a permission guard that checks if the user has the required permission
 * @param permissionCheck - Function that returns true if user has permission
 * @param redirectRoute - Route to redirect to if permission is denied (defaults to schedule)
 */
export function createPermissionGuard(
  permissionCheck: () => boolean,
  redirectRoute: string = ApplicationRoutes.SCHEDULE
): CanActivateFn {
  return () => {
    // const permissionService = inject(PermissionService);
    const router = inject(Router);

    if (permissionCheck()) {
      return true;
    }

    // User doesn't have permission, redirect to default route
    router.navigate([redirectRoute]);
    return false;
  };
}

/**
 * Guard for Active Plans route - checks ViewClientWorkoutPlans permission
 */
export const canActivateWorkoutPlans: CanActivateFn = createPermissionGuard(
  () => inject(PermissionService).canViewClientWorkoutPlans()
);

/**
 * Guard for Invoices route - checks AccessInvoices permission
 */
export const canActivateInvoices: CanActivateFn = createPermissionGuard(
  () => inject(PermissionService).canAccessInvoices()
);

/**
 * Guard for Payments route - checks AccessPayments permission
 */
export const canActivatePayments: CanActivateFn = createPermissionGuard(
  () => inject(PermissionService).canAccessPayments()
);

/**
 * Guard for Catalog route - checks AccessCatalog permission
 */
export const canActivateCatalog: CanActivateFn = createPermissionGuard(
  () => inject(PermissionService).canAccessCatalog()
);

/**
 * Guard for Catalog redirect route - checks AccessCatalog permission and redirects to services
 * If no permission, redirects to schedule
 */
export const canActivateCatalogRedirect: CanActivateFn = () => {
  const permissionService = inject(PermissionService);
  const router = inject(Router);

  if (permissionService.canAccessCatalog()) {
    router.navigate([ApplicationRoutes.CATALOG_SERVICES], { replaceUrl: true });
    return false;
  }

  // User doesn't have access, redirect to schedule
  router.navigate([ApplicationRoutes.SCHEDULE], { replaceUrl: true });
  return false;
};

/**
 * Guard for Employees route - checks AccessEmployees permission
 */
export const canActivateEmployees: CanActivateFn = createPermissionGuard(
  () => inject(PermissionService).canAccessEmployees()
);

/**
 * Guard for Employee redirect route - checks AccessEmployees permission and redirects to team members
 * If no permission, redirects to schedule
 */
export const canActivateEmployeeRedirect: CanActivateFn = () => {
  const permissionService = inject(PermissionService);
  const router = inject(Router);

  if (permissionService.canAccessEmployees()) {
    router.navigate([ApplicationRoutes.EMPLOYEE_TEAM_MEMBERS], { replaceUrl: true });
    return false;
  }

  // User doesn't have access, redirect to schedule
  router.navigate([ApplicationRoutes.SCHEDULE], { replaceUrl: true });
  return false;
};

/**
 * Guard for Settings route - checks AccessSettings permission
 */
export const canActivateSettings: CanActivateFn = createPermissionGuard(
  () => inject(PermissionService).canAccessSettings()
);

/**
 * Guard for Statistics route - redirects to the first available route based on permissions
 * Priority: Payments > Invoices > Schedule (fallback)
 */
export const canActivateStatistics: CanActivateFn = () => {
  const permissionService = inject(PermissionService);
  const router = inject(Router);

  // Check permissions and redirect to the first available route
  if (permissionService.canAccessPayments()) {
    router.navigate([ApplicationRoutes.STATISTICS_PAYMENTS], { replaceUrl: true });
    return false;
  }

  if (permissionService.canAccessInvoices()) {
    router.navigate([ApplicationRoutes.STATISTICS_INVOICES], { replaceUrl: true });
    return false;
  }

  // User doesn't have access to either, redirect to schedule
  router.navigate([ApplicationRoutes.SCHEDULE], { replaceUrl: true });
  return false;
};

